import {
  DesktopIcon,
  EnvelopeClosedIcon,
  EnvelopeOpenIcon,
  ExclamationTriangleIcon,
  ExitIcon,
  MixerHorizontalIcon,
} from '@radix-ui/react-icons';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonNavMenu } from 'components/common/layout/nav-menu';
import { ConnectLiveboardDialog } from 'components/common/liveboard/connect-liveboard-dialog';
import { SettingsDialog } from 'components/common/settings-dialog';
import { MachineConnectionTrigger } from 'components/main/sidebar/footer/connection-trigger';
import { ConnectionWarning } from 'components/main/sidebar/footer/connection-warning';
import { SupportMenu } from 'components/main/sidebar/footer/support-menu';
import { SidebarNavigationTrigger } from 'components/main/sidebar/nav-trigger';
import { MachineContext, MachineDialogMode } from 'contexts/machine.context';
import { SessionEventsContext } from 'contexts/session-events.context';
import { SettingsSection } from 'enums/settings';
import useLiveboard from 'hooks/useLiveboard';
import useLogout from 'hooks/useLogout';
import { t } from 'i18next';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { SpecialMsPosition } from 'lib_ts/interfaces/machine-msg/i-special-mstarget';
import { useContext } from 'react';
import useInboxStore from 'stores/use-inbox-store';

const Logout = () => {
  const { logout } = useLogout();
  const { specialMsTarget } = useContext(MachineContext);

  return (
    <SidebarNavigationTrigger
      icon={<ExitIcon />}
      label="main.logout"
      onClick={() => {
        specialMsTarget(SpecialMsPosition.home);
        logout({ reason: 'logout' });
      }}
      small
    />
  );
};

const SidebarInbox = () => {
  const inbox = useInboxStore((s) => s.inbox);

  if (inbox.length === 0) {
    return null;
  }

  return (
    <SidebarNavigationTrigger
      icon={inbox.length === 0 ? <EnvelopeClosedIcon /> : <EnvelopeOpenIcon />}
      label={t('main.inbox-x', { x: inbox.length }).toString()}
      onClick={() => useInboxStore.getState().setDialog(Date.now())}
      small
    />
  );
};

const LiveView = () => {
  const { lastBallCount, openDialog, isActive } = useContext(MachineContext);

  if (lastBallCount === undefined || !isActive) {
    return null;
  }

  if (lastBallCount === 1) {
    return null;
  }

  return (
    <SidebarNavigationTrigger
      icon={<ExclamationTriangleIcon />}
      color={RADIX.COLOR.WARNING}
      label="main.inspect"
      onClick={() => openDialog(MachineDialogMode.Inspect)}
      small
    />
  );
};

const Settings = () => {
  const { machine } = useContext(MachineContext);
  const { dialogSettings, setDialogSettings } =
    useContext(SessionEventsContext);

  if (!machine._id) {
    return null;
  }

  return (
    <>
      <SidebarNavigationTrigger
        data-testid="SettingsButton"
        icon={<MixerHorizontalIcon />}
        label="main.settings"
        onClick={() =>
          setDialogSettings({
            identifier: 'SidebarFooterSettingsDialog',
            initialSection: SettingsSection.Machine,
            onClose: () => setDialogSettings(undefined),
          })
        }
        small
      />
      {dialogSettings && <SettingsDialog {...dialogSettings} />}
    </>
  );
};

const ConnectLiveboard = () => {
  const { machine } = useContext(MachineContext);
  const {
    connectLiveboardDialog,
    openConnectLiveboardDialog,
    closeConnectLiveboardDialog,
    openLiveboard,
  } = useLiveboard();

  if (!machine.feature_flags?.liveboard) {
    return null;
  }

  return (
    <>
      <SidebarNavigationTrigger
        icon={<DesktopIcon />}
        label={t('settings.live-monitor')}
        actions={[
          {
            label: t('settings.open-live-monitor'),
            onClick: openLiveboard,
          },
          {
            label: t('settings.enter-live-monitor-code'),
            onClick: openConnectLiveboardDialog,
          },
        ]}
        small
      />
      {connectLiveboardDialog && (
        <ConnectLiveboardDialog
          key={connectLiveboardDialog}
          identifier="RecapConnectLiveboardDialog"
          onClose={closeConnectLiveboardDialog}
        />
      )}
    </>
  );
};

export const SidebarFooter = () => {
  return (
    <ErrorBoundary identifier="SidebarFooter">
      <CommonNavMenu orientation="vertical">
        <ConnectionWarning />

        <SidebarInbox />
        <Settings />
        <LiveView />
        <MachineConnectionTrigger as="nav" />
        <ConnectLiveboard />
        <SupportMenu />
        <Logout />
      </CommonNavMenu>
    </ErrorBoundary>
  );
};
